import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useStore } from "../context/StoreContext";
import { api, COUNTRIES, zoneOf, formatEUR, formatApiErrorDetail } from "../lib/api";
import Placeholder from "../components/Placeholder";
import { Reveal } from "../components/Reveal";

const ZONE_LABELS = { france: "France", europe: "Europe", international: "International" };

const Checkout = () => {
  const { cart, subtotal, settings, clearCart } = useStore();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "", city: "", postal_code: "", country: "FR",
    delivery_method: "domicile", relais_name: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const saved = localStorage.getItem("moha66_country");
    if (saved) setForm((f) => ({ ...f, country: saved }));
  }, []);

  const zone = zoneOf(form.country);
  const isRelais = form.delivery_method === "relais" && zone === "france";
  const shippingFee = useMemo(() => {
    if (!settings) return null;
    const sh = settings.shipping || {};
    if (sh.free_threshold > 0 && subtotal >= sh.free_threshold) return 0;
    return sh[zone] ?? 0;
  }, [settings, subtotal, zone]);
  const total = subtotal + (shippingFee || 0);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (cart.length === 0) {
    return (
      <main className="pt-40 pb-32 text-center px-4 min-h-screen" data-testid="checkout-empty">
        <p className="font-display font-extrabold uppercase text-outline text-6xl">Panier vide</p>
        <p className="mt-4 text-sm text-neutral-400">Ajoutez des pièces avant de passer commande.</p>
        <button onClick={() => navigate("/boutique")} data-testid="checkout-empty-shop-button" className="mt-8 bg-white text-ink font-mono text-xs uppercase tracking-[0.25em] px-10 py-4 hover:bg-gold transition-colors">
          Voir la boutique
        </button>
      </main>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.address || !form.city || !form.postal_code) {
      setError("Merci de remplir tous les champs obligatoires.");
      return;
    }
    if (isRelais && (!form.relais_name.trim() || !form.phone.trim())) {
      setError("Pour un point relais : le nom du point relais et votre téléphone sont obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      localStorage.setItem("moha66_country", form.country);
      const data = await api.post("/checkout", {
        items: cart.map((i) => ({ product_id: i.product_id, size: i.size, color: i.color, quantity: i.quantity, custom_text: i.custom_text || "" })),
        customer: { ...form, email: form.email.trim() },
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(formatApiErrorDetail(err.message));
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-panel border border-line focus:border-white outline-none px-4 py-3 text-sm text-white placeholder:text-neutral-600 transition-colors";

  return (
    <main className="pt-28 sm:pt-36 pb-24 px-4 sm:px-8 lg:px-16 min-h-screen" data-testid="checkout-page">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-500">Étape finale</p>
        <h1 className="mt-3 font-display font-extrabold uppercase tracking-tighter text-4xl sm:text-6xl text-white">
          Check<span className="text-outline-strong">out</span>
        </h1>
      </Reveal>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* ----- Formulaire ----- */}
        <form onSubmit={submit} className="lg:col-span-7 space-y-8" data-testid="checkout-form">
          <section className="border border-line bg-coal p-6 sm:p-8">
            <h2 className="font-display font-bold uppercase tracking-wider text-lg text-white">1 · Vos coordonnées</h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label htmlFor="ck-name" className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Nom complet *</label>
                <input id="ck-name" data-testid="checkout-name-input" required value={form.name} onChange={set("name")} className={`mt-2 ${inputCls}`} placeholder="Prénom Nom" />
              </div>
              <div>
                <label htmlFor="ck-email" className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Email *</label>
                <input id="ck-email" data-testid="checkout-email-input" type="email" required value={form.email} onChange={set("email")} className={`mt-2 ${inputCls}`} placeholder="vous@exemple.com" />
              </div>
              <div>
                <label htmlFor="ck-phone" className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{isRelais ? "Téléphone *" : "Téléphone"}</label>
                <input id="ck-phone" data-testid="checkout-phone-input" value={form.phone} onChange={set("phone")} className={`mt-2 ${inputCls}`} placeholder="06 12 34 56 78" />
              </div>
            </div>
          </section>

          <section className="border border-line bg-coal p-6 sm:p-8">
            <h2 className="font-display font-bold uppercase tracking-wider text-lg text-white">{isRelais ? "2 · Point relais" : "2 · Adresse de livraison"}</h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="checkout-delivery-method">
              <button type="button" data-testid="checkout-delivery-domicile" onClick={() => setForm((f) => ({ ...f, delivery_method: "domicile" }))}
                className={`border px-4 py-4 text-left font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${!isRelais ? "border-white bg-white text-ink" : "border-line text-neutral-400 hover:border-neutral-500"}`}>
                À domicile
              </button>
              {zone === "france" && (
                <button type="button" data-testid="checkout-delivery-relais" onClick={() => setForm((f) => ({ ...f, delivery_method: "relais" }))}
                  className={`border px-4 py-4 text-left font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${isRelais ? "border-white bg-white text-ink" : "border-line text-neutral-400 hover:border-neutral-500"}`}>
                  Point relais Mondial Relay
                </button>
              )}
            </div>
            {isRelais && (
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500 leading-relaxed">
                Trouvez votre point relais sur mondialrelay.fr puis indiquez ses coordonnées ci-dessous — même tarif que la France.
              </p>
            )}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {isRelais && (
                <div className="sm:col-span-2">
                  <label htmlFor="ck-relais" className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Nom du point relais *</label>
                  <input id="ck-relais" data-testid="checkout-relais-input" value={form.relais_name} onChange={set("relais_name")} className={`mt-2 ${inputCls}`} placeholder="Ex. Tabac Presse du Centre — 66000" />
                </div>
              )}
              <div className="sm:col-span-2">
                <label htmlFor="ck-address" className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{isRelais ? "Adresse du point relais *" : "Adresse *"}</label>
                <input id="ck-address" data-testid="checkout-address-input" required value={form.address} onChange={set("address")} className={`mt-2 ${inputCls}`} placeholder={isRelais ? "12 rue de la Gare" : "12 rue de la Street"} />
              </div>
              <div>
                <label htmlFor="ck-city" className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Ville *</label>
                <input id="ck-city" data-testid="checkout-city-input" required value={form.city} onChange={set("city")} className={`mt-2 ${inputCls}`} placeholder="Perpignan" />
              </div>
              <div>
                <label htmlFor="ck-zip" className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Code postal *</label>
                <input id="ck-zip" data-testid="checkout-zip-input" required value={form.postal_code} onChange={set("postal_code")} className={`mt-2 ${inputCls}`} placeholder="66000" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="ck-country" className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Pays *</label>
                <select id="ck-country" data-testid="checkout-country-select" value={form.country}
                  onChange={(e) => setForm((f) => ({ ...f, country: e.target.value, delivery_method: zoneOf(e.target.value) === "france" ? f.delivery_method : "domicile" }))}
                  className={`mt-2 ${inputCls}`}>
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          <section className="border border-line bg-coal p-6 sm:p-8">
            <h2 className="font-display font-bold uppercase tracking-wider text-lg text-white">3 · Paiement</h2>
            <p className="mt-3 text-sm text-neutral-400 leading-relaxed">
              Vous serez redirigé vers <span className="text-white font-semibold">Stripe Checkout</span> pour régler en toute sécurité.
              Moha66 ne voit jamais vos données bancaires et ne les stocke jamais.
            </p>
            <button
              type="submit"
              data-testid="checkout-submit-button"
              disabled={submitting}
              className="mt-6 w-full bg-white text-ink font-mono text-xs uppercase tracking-[0.25em] py-4 hover:bg-gold disabled:opacity-50 transition-colors duration-300"
            >
              {submitting ? "Redirection vers Stripe…" : `Payer ${formatEUR(total)} — Stripe Checkout`}
            </button>
            {error && <p className="mt-4 font-mono text-xs text-red-400" data-testid="checkout-error">{error}</p>}
          </section>
        </form>

        {/* ----- Récapitulatif ----- */}
        <aside className="lg:col-span-5">
          <div className="border border-line bg-coal p-6 sm:p-8 lg:sticky lg:top-28" data-testid="checkout-summary">
            <h2 className="font-display font-bold uppercase tracking-wider text-lg text-white">Récapitulatif</h2>
            <div className="mt-6 space-y-4 max-h-72 overflow-y-auto pr-1">
              {cart.map((item, idx) => (
                <div key={idx} className="flex gap-4 items-center" data-testid="checkout-summary-item">
                  <div className="w-14 h-16 shrink-0 border border-line overflow-hidden">
                    {item.image ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" /> : <Placeholder />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold uppercase tracking-wider text-white truncate">{item.name}</p>
                    <p className="font-mono text-[11px] text-neutral-500">{item.size}{item.color ? ` · ${item.color}` : ""}{item.custom_text ? ` · N° ${item.custom_text}` : ""} × {item.quantity}</p>
                  </div>
                  <p className="font-mono text-sm text-silver">{formatEUR(item.price * item.quantity)}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-line space-y-2 text-sm">
              <div className="flex justify-between text-neutral-400">
                <span>Sous-total</span>
                <span data-testid="checkout-subtotal" className="font-mono text-white">{formatEUR(subtotal)}</span>
              </div>
              <div className="flex justify-between text-neutral-400">
                <span>Livraison · {isRelais ? "Point relais" : ZONE_LABELS[zone]}</span>
                <span data-testid="checkout-shipping" className="font-mono text-white">
                  {shippingFee === null ? "—" : shippingFee === 0 ? "Offerte" : formatEUR(shippingFee)}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-line text-base">
                <span className="font-display font-bold uppercase text-white">Total</span>
                <span data-testid="checkout-total" className="font-mono font-bold text-white">{formatEUR(total)}</span>
              </div>
            </div>
            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-600 leading-relaxed">
              Mode test Stripe actif — carte de test 4242 4242 4242 4242. Aucune donnée bancaire n'est stockée par Moha66.
            </p>
            <Link to="/livraison" className="mt-3 inline-block font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500 underline underline-offset-4 hover:text-white">
              Voir les frais de livraison
            </Link>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default Checkout;
